import { DOCUMENT, DestroyRef, Injectable, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import {
  ChatAction,
  ChatMessage,
  ChatbotQueryFilter,
  ChatbotTableFilter,
  ChatbotTableFilterSpec,
  ContextKind,
  ContextTerm,
  Intent,
  MapSection,
  SemanticAnalysis
} from "./chatbot.types";
import { normalizeChatbotText } from "./chatbot-normalize.util";
import { CHATBOT_INTENTS } from "./chatbot-intents.config";
import { APP_MAP_SECTIONS } from "./chatbot-map.config";
import { ChatbotNavigationService, NavigationMatch } from "./chatbot-navigation.service";
import { ChatbotNlpService } from "./chatbot-nlp.service";
import { CHATBOT_MONTH_NAMES, detectDatePeriod, todayDateRange } from "./chatbot-date.util";
import { detectQueryFilter } from "./chatbot-query-filter.util";
import { collectMatchedIntents, decideIntent, NlpMatch } from "./chatbot-intent-resolver";
import {
  buildScreenHelpMessage,
  findMenuEntryForUrl,
  findScreenForUrl,
  findSectionForUrl,
  readScreenSnapshot
} from "./chatbot-screen-help.util";
import { CHATBOT_SCREENS } from "./chatbot-screens.config";
import { ChatbotScreenRegistryService } from "./chatbot-screen-registry.service";
import { MENU } from "../../modules/nav-module/navbar-side/navbar-menu";
import { SessionDataService } from "../../services/shared/SessionDataService.service";
import { DateRange } from "../../interfaces/DatesGlobalInterface.type";
import dayjs from "../../configs/Dayjs.config";
import { PROFILES } from "../../../assets/profiles/profiles";

const TYPING_DELAY_MS = 350;
/** Lets the user read the bot reply before the route changes. */
const NAVIGATION_DELAY_MS = 400;
const MAX_LISTED_MATCHES = 10;
const WELCOME_ID = "welcome";
const MODEL_SEARCH_INTENT_ID = "navigate-mantenimiento-busqueda-por-modelo";

type PendingChoice =
  | {
      kind: "context";
      matches: NavigationMatch[];
      navIntent: Intent | null;
      dateRange: DateRange | null;
      queryFilter: ChatbotQueryFilter | null;
    }
  | { kind: "intent"; choices: Intent[]; analysis: SemanticAnalysis };

// ANCHOR: Text extraction patterns (normalised text)

/** Leading navigation verbs, including "go to", "show", and "switch to". */
const RE_LEADING_VERB =
  /^(?:ir\s+a|ir\s+para|navegar?\s+a|ver|veure|visualizar|mostrar|muestra|go\s+to|navigate\s+to|show(?:\s+me)?|view|find|seleccionar|select|switch\s+to|change\s+to|cambiar(?:\s+a)?|canviar(?:\s+a)?|anar\s+a|aller\s+a|montrer|voir|vai\s+a|mudar(?:\s+para)?|search(?:\s+for)?|buscar?)\s+/;
const RE_LEADING_ARTICLE = /^(?:a\s+la|al|a|to|la|le|el|the|il|l)\s+/;
const RE_LEADING_CONTEXT_NOUN =
  /^(?:agrupacion(?:es)?|agrupaments?|agrupations?|agrupazione|agrupacao|groupements?|raggruppament[io]|zona|zone|sector|entidad(?:es)?|entidade|entity|entities|entites?|entitat|entita|clientes?|clients?|clienti)\s+/;

const RE_CONTEXT_AGRUPATION =
  /\b(?:agrupacion(?:es)?|agrupaments?|agrupations?|zona|sector|zone)\s+(?:del?\s+)?(\w+(?:\s+\w+){0,3})/;
const RE_CONTEXT_ENTITY =
  /\b(?:entidad(?:es)?|entidade|entity|entities|entite|entitat|entita)\s+(?:del?\s+)?(\w+(?:\s+\w+){0,3})/;
/** Words that end a captured context name ("agrupacion arguedas en el ultimo mes" → "arguedas"). */
const CONTEXT_NAME_STOP_WORDS: ReadonlySet<string> = new Set([
  "en", "la", "el", "los", "las", "the", "to", "and", "y", "con", "por", "para", "in", "on", "of",
  "with", "desde", "hasta", "durante", "hoy", "ayer", "este", "esta", "ultimo", "ultima", "ultimos",
  "ultimas", "last", "this", "today", "yesterday", "que",
  // Context nouns must not be captured as part of a name.
  "entidad", "entidades", "entity", "agrupacion", "agrupaciones", "zona", "cliente", "clientes",
  "mantenimiento", "configuracion"
]);
const RE_TRAILING_PREPOSITION = /(?:\s+(?:de|del))+$/;

/** Boilerplate removed from model-search queries, leaving the manufacturer / model name. */
const RE_MODEL_SEARCH_NOISE =
  /\b(?:cuantos?|contadores?|medidores?|dispositivos?|hay|existentes?|existen?|tienen?|busqueda|buscar|modelos?|fabricantes?|global|agrupacion|entidad|zona|como|cuales?|por|del?|en|la|el|los|las|un|una|al|que)\b/g;

/** Navigation/UI words excluded from implicit proper-noun context detection. */
const BOT_VOCAB: ReadonlySet<string> = new Set([
  "ver", "ir", "ira", "mostrar", "navegar", "buscar", "cambiar", "seleccionar", "volver", "show",
  "navigate", "find", "switch", "change", "select", "view", "open", "close", "go", "quiero",
  "necesito", "please", "porfavor", "alarmas", "alertas", "avisos", "dispositivos", "medidores",
  "contadores", "gateways", "clientes", "usuarios", "analisis", "graficas", "cobertura", "mapa",
  "mantenimiento", "telecontrol", "ficheros", "cups", "agrupaciones", "entidades", "sensorica",
  "ordenes", "instalador", "instalacion", "activacion", "devices", "meters", "alarms", "clients",
  "users", "analysis", "map", "inicio", "home", "dashboard", "principal", "hola", "hello", "buenos",
  "buenas", "dias", "tardes", "noches", "ayuda", "del", "los", "las", "una", "uno", "unos", "unas",
  "para", "con", "por", "the", "and", "from", "this", "that", "these", "those", "hoy", "ayer",
  "semana", "mes", "ano", "ultimo", "ultima", "pasado", "today", "yesterday", "week", "month",
  "year", "last", "next",
  // Query / filter terms
  "fugas", "fuga", "leaks", "leak", "consultar", "consulta", "consultas", "desde", "hasta", "entre",
  "durante",
  // Pronouns / question words capitalised at sentence start
  "me", "mi", "tu", "te", "se", "yo", "si", "le", "nos", "cuantos", "cuantas", "cuales", "que",
  "como", "donde", "hay",
  // Common words that fuzzy-match place names
  "error", "errores", "fallo", "fallos", "problema", "problemas", "correcto", "correcta",
  "incorrecto", "incorrecta", "totalmente", "existe", "existia", "tiene", "tienen", "habia",
  "habian", "gustaria", "quisiera", "obtener", "saber", "hacer", "lanzar"
]);

/** True for words that can never be a context name (bot vocabulary or month). */
function isVocabWord(word: string): boolean {
  return BOT_VOCAB.has(word) || CHATBOT_MONTH_NAMES.has(word);
}

function escapeRegExp(text: string): string {
  return text.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

/** Strips leading verbs, articles and the context noun: "ir a la agrupacion Zona Norte" → "zona norte". */
export function extractSearchTerm(normalized: string): string {
  return normalized
    .replace(RE_LEADING_VERB, "")
    .replace(RE_LEADING_ARTICLE, "")
    .replace(RE_LEADING_CONTEXT_NOUN, "")
    .replace(RE_LEADING_ARTICLE, "")
    .trim();
}

/** Keeps the words of a captured context name up to the first stop word. */
function trimContextName(captured: string): string | null {
  const words: string[] = [];
  for (const word of captured.split(" ")) {
    if (CONTEXT_NAME_STOP_WORDS.has(word)) {
      break;
    }
    words.push(word);
  }
  const name = words.join(" ").replace(RE_TRAILING_PREPOSITION, "").trim();
  return name.length > 1 ? name : null;
}

/** Context name introduced by an explicit keyword ("agrupacion X", "zona X", "entidad X"). */
export function extractExplicitContext(normalized: string): ContextTerm | null {
  const agrupation = RE_CONTEXT_AGRUPATION.exec(normalized);
  const agrupationName = agrupation && trimContextName(agrupation[1]);
  if (agrupationName) {
    return { term: agrupationName, kind: "agrupation", implicit: false };
  }
  const entity = RE_CONTEXT_ENTITY.exec(normalized);
  const entityName = entity && trimContextName(entity[1]);
  return entityName ? { term: entityName, kind: "entity", implicit: false } : null;
}

/** Manufacturer / model name left after removing model-search boilerplate and the context name. */
export function extractModelSearchTerm(normalized: string, contextTerm: string | null): string | null {
  let text = normalized;
  if (contextTerm) {
    text = text.replaceAll(new RegExp(String.raw`\b${escapeRegExp(contextTerm)}\b`, "g"), "");
  }
  const stripped = text.replaceAll(RE_MODEL_SEARCH_NOISE, "").replaceAll(/\s+/g, " ").trim();
  return stripped || null;
}

/** Chatbot orchestrator: parses user text, navigates, switches context and answers screen-help questions. */
@Injectable({ providedIn: "root" })
export class ChatbotService {
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly navService = inject(ChatbotNavigationService);
  private readonly nlpService = inject(ChatbotNlpService);
  private readonly sessionDataService = inject(SessionDataService);
  private readonly document = inject(DOCUMENT);
  private readonly screenRegistry = inject(ChatbotScreenRegistryService);

  // ANCHOR: State

  /**
   * Concept-map sections available to the current profile and client.
   * Mirrors profile, `client`, and `visible: "gas"` restrictions from navbar-menu.ts.
   */
  readonly sectionMap = computed<MapSection[]>(() => {
    const profile = this.sessionDataService.profile();
    if (!profile) {
      return [];
    }
    const client = this.sessionDataService.client();
    return APP_MAP_SECTIONS.filter(
      s =>
        s.profiles.includes(profile) &&
        (!s.clientIds?.length || (!!client && s.clientIds.includes(client.clientId))) &&
        (!s.requiresGas || !!client?.gas)
    );
  });

  readonly isArsonProfile = computed<boolean>(
    () => this.sessionDataService.profile() === PROFILES.ARSON
  );

  readonly chatbotTitle = computed<string>(() =>
    this.sessionDataService.client()?.gas ? "GasBot" : "AquaBot"
  );

  private readonly _messages = signal<ChatMessage[]>([this.welcomeMessage()]);
  readonly messages = this._messages.asReadonly();

  private readonly _isTyping = signal<boolean>(false);
  readonly isTyping = this._isTyping.asReadonly();

  /** Numbered choice awaiting the user's reply. */
  private pending: PendingChoice | null = null;

  constructor() {
    // Welcome text may be built before translations load; keep it in sync with the language.
    this.translate
      .stream("chatbot.welcome")
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(() =>
        this._messages.update(msgs =>
          msgs.map(m => (m.id === WELCOME_ID ? { ...m, content: this.translateKey("chatbot.welcome") } : m))
        )
      );
  }

  // ANCHOR: Public API

  /** Navigates directly to a route payload (used by the concept map overlay). */
  navigateTo(payload: string): void {
    this.router.navigate(["/", ...payload.split("/")]);
  }

  /** Resets the conversation to the welcome message and clears pending state. */
  clearMessages(): void {
    this.pending = null;
    this._messages.set([this.welcomeMessage()]);
  }

  /** Explains the current route and topmost open dialog, with interaction tips. */
  explainCurrentScreen(): void {
    this.pending = null;
    // Snapshot now: the screen may change during the typing delay.
    const url = this.router.url;
    const context = {
      screen: findScreenForUrl(url, CHATBOT_SCREENS),
      menuEntry: findMenuEntryForUrl(url, MENU),
      section: findSectionForUrl(url, APP_MAP_SECTIONS),
      snapshot: readScreenSnapshot(this.document, this.screenRegistry.widgets())
    };
    this.push(this.translateKey("chatbot.screen-help.request"), "user", "screen-help");
    this._isTyping.set(true);
    setTimeout(() => {
      this.push(buildScreenHelpMessage(context, (key, params) => this.translateKey(key, params)), "bot", "screen-help");
      this._isTyping.set(false);
    }, TYPING_DELAY_MS);
  }

  /** Pushes the user message and answers after a short typing delay. */
  sendMessage(text: string): void {
    const trimmed = text.trim();
    if (!trimmed) {
      return;
    }
    this.push(trimmed, "user");
    this._isTyping.set(true);
    setTimeout(() => {
      this.handleInput(trimmed)
        .catch(() => this.push(this.translateKey("chatbot.unknown"), "bot"))
        .finally(() => this._isTyping.set(false));
    }, TYPING_DELAY_MS);
  }

  // ANCHOR: Input handling

  /** Pending numbered choice first; otherwise full analysis + dispatch. */
  private async handleInput(text: string): Promise<void> {
    const pending = this.pending;
    this.pending = null;
    if (pending && this.resolvePendingChoice(pending, text)) {
      return;
    }

    const normalized = normalizeChatbotText(text);
    const nlpPromise = this.nlpService.classifyIntent(normalized);
    const matches = collectMatchedIntents(normalized);
    const dateRange = detectDatePeriod(normalized);
    const analysis: SemanticAnalysis = {
      normalized,
      dateRange,
      queryFilter: detectQueryFilter(normalized, dateRange !== null),
      context:
        extractExplicitContext(normalized) ??
        this.extractImplicitContext(text, normalized, !matches.primaryIntent || dateRange !== null)
    };

    const nlpRaw = await nlpPromise;
    const nlp: NlpMatch = {
      intent: nlpRaw ? (CHATBOT_INTENTS.find(i => i.id === nlpRaw.intent) ?? null) : null,
      score: nlpRaw?.score ?? 0
    };
    const decision = decideIntent(
      matches,
      { dateRange, queryFilter: analysis.queryFilter, hasContextTerm: analysis.context !== null },
      nlp
    );

    if (decision.tiedWith) {
      this.askIntentChoice(analysis, [decision.navIntent, decision.tiedWith]);
      return;
    }
    if (decision.navIntent || dateRange || analysis.queryFilter || analysis.context) {
      this.dispatch(analysis, decision.navIntent);
      return;
    }

    const simple = decision.simpleIntent;
    if (simple.action === ChatAction.SET_CONTEXT && simple.contextKind) {
      const term = extractSearchTerm(normalized);
      if (term) {
        this.applyContextSwitch({ term, kind: simple.contextKind, implicit: false }, null, null, null);
      } else {
        this.push(this.translateKey("chatbot.context-missing-term"), "bot");
      }
      return;
    }
    this.push(this.translateKey(simple.response || "chatbot.unknown"), "bot");
  }

  /** Returns true when the reply picked a valid numbered option. */
  private resolvePendingChoice(pending: PendingChoice, text: string): boolean {
    const options = pending.kind === "context" ? pending.matches : pending.choices;
    const index = Number.parseInt(text.trim(), 10) - 1;
    if (!(index >= 0 && index < Math.min(options.length, MAX_LISTED_MATCHES))) {
      return false;
    }
    if (pending.kind === "context") {
      this.applyMatch(pending.matches[index], pending.navIntent, pending.dateRange, pending.queryFilter);
    } else {
      this.dispatch(pending.analysis, pending.choices[index]);
    }
    return true;
  }

  private askIntentChoice(analysis: SemanticAnalysis, choices: Intent[]): void {
    this.pending = { kind: "intent", choices, analysis };
    const lines = choices.map((c, i) => `  ${i + 1}. ${c.response ? this.translateKey(c.response) : c.id}`);
    this.push([this.translateKey("chatbot.intent-ambiguous"), ...lines].join("\n"), "bot");
  }

  // ANCHOR: Dispatch

  /** Context switch and/or navigation, carrying date range + query filter to the target screen. */
  private dispatch(analysis: SemanticAnalysis, navIntent: Intent | null): void {
    const { normalized, dateRange, queryFilter } = analysis;
    // For model search an implicit term is the manufacturer name, not a context to switch to.
    const context =
      navIntent?.id === MODEL_SEARCH_INTENT_ID && analysis.context?.implicit ? null : analysis.context;

    if (navIntent?.id === MODEL_SEARCH_INTENT_ID) {
      const modelTerm = extractModelSearchTerm(normalized, context?.term ?? null);
      if (modelTerm) {
        this.sessionDataService.sendChatbotModelFilter(modelTerm);
      }
    }

    if (context) {
      this.applyContextSwitch(context, navIntent, dateRange, queryFilter);
      return;
    }

    const { range, defaulted } = this.effectiveDateRange(dateRange, queryFilter);
    if (navIntent?.payload) {
      this.navigateToIntent(navIntent, range, queryFilter);
      if (defaulted) {
        this.push(this.translateKey("chatbot.date-defaulted-today"), "bot");
      }
    } else if (range) {
      this.push(this.translateKey("chatbot.date-set"), "bot");
      this.broadcastSignals(range, queryFilter);
    }
  }

  /** Searches the context term and applies it, or asks the user to pick among matches. */
  private applyContextSwitch(
    context: ContextTerm,
    navIntent: Intent | null,
    dateRange: DateRange | null,
    queryFilter: ChatbotQueryFilter | null
  ): void {
    const { range, defaulted } = this.effectiveDateRange(dateRange, queryFilter);
    const matches = this.findMatches(context.term, context.kind);

    if (matches.length === 0) {
      this.push(this.translateKey("chatbot.context-not-found", { term: context.term }), "bot");
      if (navIntent?.payload) {
        this.navigateToIntent(navIntent, range, queryFilter);
      } else {
        this.broadcastSignals(range, queryFilter);
      }
      return;
    }
    if (matches.length === 1) {
      if (defaulted) {
        this.push(this.translateKey("chatbot.date-defaulted-today"), "bot");
      }
      this.applyMatch(matches[0], navIntent, range, queryFilter);
      return;
    }
    this.pending = { kind: "context", matches, navIntent, dateRange: range, queryFilter };
    const lines = matches.slice(0, MAX_LISTED_MATCHES).map((m, i) => `  ${i + 1}. ${m.label}`);
    this.push([this.translateKey("chatbot.context-ambiguous"), ...lines].join("\n"), "bot");
  }

  private findMatches(term: string, kind: ContextKind): NavigationMatch[] {
    switch (kind) {
      case "entity":
        return this.navService.findEntityMatches(term);
      case "client":
        return this.navService.findClientMatches(term);
      default:
        return this.navService.findAgrupationMatches(term);
    }
  }

  /** Applies a context match, then navigates (if requested) and broadcasts the signals. */
  private applyMatch(
    match: NavigationMatch,
    navIntent: Intent | null,
    dateRange: DateRange | null,
    queryFilter: ChatbotQueryFilter | null
  ): void {
    if (!navIntent?.payload) {
      this.navService.applyMatch(match);
      this.push(this.translateKey("chatbot.context-applied", { label: match.label }), "bot");
      this.broadcastSignals(dateRange, queryFilter);
      return;
    }
    // Must be armed BEFORE the context signals change (RouteCheckService consumes it).
    this.sessionDataService.sendChatbotNavigating(true);
    this.sessionDataService.sendAvoidNavigationReload(true);
    this.navService.applyMatch(match);
    this.navigateToIntent(navIntent, dateRange, queryFilter, true);
  }

  /**
   * Confirms, then navigates to the intent route after a short delay.
   * Table filters are stored before navigation so the target screen reads them on init;
   * date range / query filter are broadcast once the route is active.
   */
  private navigateToIntent(
    intent: Intent,
    dateRange: DateRange | null,
    queryFilter: ChatbotQueryFilter | null,
    contextSwitched = false
  ): void {
    if (intent.response) {
      this.push(this.translateKey(intent.response), "bot");
    }
    if (intent.tableFilter) {
      this.sessionDataService.sendChatbotTableFilter(this.computeTableFilter(intent.tableFilter));
    }
    const segments = intent.payload.split("/");
    const extras = intent.payloadHistoryData ? { state: intent.payloadHistoryData } : undefined;

    setTimeout(() => {
      // Already there: screens react to the broadcast signals without a route change.
      if (!intent.tableFilter && !contextSwitched && this.router.url === "/" + intent.payload) {
        this.broadcastSignals(dateRange, queryFilter);
        return;
      }
      if (intent.tableFilter) {
        this.sessionDataService.sendUrlReloadFlag(true);
      }
      this.router
        .navigate(["/", ...segments], extras)
        .then(() => this.broadcastSignals(dateRange, queryFilter))
        .finally(() => {
          if (contextSwitched) {
            this.sessionDataService.sendChatbotNavigating(false);
            this.sessionDataService.sendAvoidNavigationReload(false);
          }
        });
    }, NAVIGATION_DELAY_MS);
  }

  /** Query screens need a period: default to today when a query filter has no date. */
  private effectiveDateRange(
    dateRange: DateRange | null,
    queryFilter: ChatbotQueryFilter | null
  ): { range: DateRange | null; defaulted: boolean } {
    if (!dateRange && queryFilter) {
      return { range: todayDateRange(), defaulted: true };
    }
    return { range: dateRange, defaulted: false };
  }

  private broadcastSignals(dateRange: DateRange | null, queryFilter: ChatbotQueryFilter | null): void {
    if (dateRange) {
      this.sessionDataService.sendChatbotDateRange(dateRange);
    }
    if (queryFilter) {
      this.sessionDataService.sendChatbotQueryFilter(queryFilter);
    }
  }

  /** Resolves a table filter spec: translated text filter, or date cutoff relative to today. */
  private computeTableFilter(spec: ChatbotTableFilterSpec): ChatbotTableFilter {
    if (spec.i18nValue !== undefined) {
      const translated = this.translate.instant(spec.i18nValue);
      return {
        columnTitle: spec.columnTitle,
        filterData: translated,
        filterDataString: translated,
        espComparison: false,
        showNull: false,
        i18nKey: spec.i18nValue
      };
    }
    const cutoff = dayjs().subtract(spec.relativeDays, "day").startOf("day");
    return {
      columnTitle: spec.columnTitle,
      filterData: spec.operator + String(cutoff.valueOf()),
      filterDataString: spec.operator + " " + cutoff.format("YYYY-MM-DD"),
      espComparison: true,
      showNull: false
    };
  }

  // ANCHOR: Implicit context detection

  /**
   * Finds agrupation / entity / client names without an explicit keyword.
   * Strategy A: capitalised words (proper-noun heuristic), up to 10 matches.
   * Strategy B (`allowLowercase`): lowercase words ≥ 4 chars, unique match only.
   */
  private extractImplicitContext(
    original: string,
    normalized: string,
    allowLowercase: boolean
  ): ContextTerm | null {
    const capitalised = original
      .split(/\s+/)
      .map(word => word.replaceAll(/[^a-zA-Z\u00C0-\u024F]/g, ""))
      .filter(
        w => w.length >= 2 && /^[A-Z\u00C0-\u024F]/.test(w) && !isVocabWord(normalizeChatbotText(w))
      );
    const fromCapitalised = this.matchFirstContext(capitalised, MAX_LISTED_MATCHES);
    if (fromCapitalised || !allowLowercase) {
      return fromCapitalised;
    }
    // Unique hit only: common words ("error") can fuzzy-match place names.
    const lowercase = normalized.split(" ").filter(w => w.length >= 4 && !isVocabWord(w));
    return this.matchFirstContext(lowercase, 1);
  }

  /** Tries bigrams, then single words. */
  private matchFirstContext(words: string[], maxMatches: number): ContextTerm | null {
    for (let i = 0; i < words.length - 1; i++) {
      const hit = this.checkContextTerm(`${words[i]} ${words[i + 1]}`, maxMatches);
      if (hit) {
        return hit;
      }
    }
    for (const word of words) {
      const hit = this.checkContextTerm(word, maxMatches);
      if (hit) {
        return hit;
      }
    }
    return null;
  }

  /** Exact name/alias beats partial; entity beats agrupation beats client within each tier. */
  private checkContextTerm(raw: string, maxMatches: number): ContextTerm | null {
    const term = normalizeChatbotText(raw);
    // ≥3 chars: 2-letter words fuzzy-match too many names.
    if (term.length < 3) {
      return null;
    }
    const isExact = (...names: (string | null | undefined)[]) =>
      names.some(n => normalizeChatbotText(n ?? "") === term);
    const candidates: { kind: ContextKind; matches: NavigationMatch[]; exact: (m: NavigationMatch) => boolean }[] = [
      { kind: "entity", matches: this.navService.findEntityMatches(term), exact: m => isExact(m.entity?.entity, m.entity?.alias) },
      { kind: "agrupation", matches: this.navService.findAgrupationMatches(term), exact: m => isExact(m.agrupation?.name, m.agrupation?.alias) },
      { kind: "client", matches: this.navService.findClientMatches(term), exact: m => isExact(m.client?.clientName) }
    ];
    const hit =
      candidates.find(c => c.matches.some(c.exact)) ??
      candidates.find(c => c.matches.length > 0 && c.matches.length <= maxMatches);
    return hit ? { term, kind: hit.kind, implicit: true } : null;
  }

  // ANCHOR: Utilities

  private welcomeMessage(): ChatMessage {
    return { id: WELCOME_ID, content: this.translateKey("chatbot.welcome"), role: "bot", timestamp: new Date() };
  }

  private push(content: string, role: "user" | "bot", format?: ChatMessage["format"]): void {
    const msg: ChatMessage = {
      id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`,
      content,
      role,
      timestamp: new Date(),
      ...(format ? { format } : {})
    };
    this._messages.update(msgs => [...msgs, msg]);
  }

  /**
   * Translates a key (raw key if missing). JSON encodes line breaks as literal "\n";
   * converted to real newlines for the pre-wrap bubble.
   */
  private translateKey(key: string, params?: Record<string, string>): string {
    const result = this.translate.instant(key, params);
    if (typeof result !== "string" || result === key) {
      return key;
    }
    return result.replaceAll(String.raw`\n`, "\n");
  }
}
